import { Fragment, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { adminBookings, adminReschedule, adminCancelBooking, adminListHolidays, adminCreateHoliday, adminDeleteHoliday, adminDeleteHolidaySlot, adminListIntensiveOffers } from "../../lib/api";
import ManualBookingForm from "../../components/ManualBookingForm";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { toast } from "sonner";
import { SLOT_TIMES, endTime, formatLongDate, ADMIN_SLOT_TIMES } from "../../lib/site";
import { extractErrorMessage } from "../../lib/errors";
import { ChevronLeft, ChevronRight, Phone, Mail, MapPin, Car, Hash, Calendar as CalendarIcon, GripVertical, Ban, Plus, Trash2, Plane, Hourglass } from "lucide-react";
import {
  DndContext,
  useDraggable,
  useDroppable,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function isoDate(d) {
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}
function startOfMonth(d) { const x = new Date(d); x.setDate(1); x.setHours(0, 0, 0, 0); return x; }
function startOfGridForMonth(d) {
  const first = startOfMonth(d);
  const wd = (first.getDay() + 6) % 7; // Mon=0..Sun=6
  const grid = new Date(first); grid.setDate(first.getDate() - wd);
  return grid;
}

// Mondays from this date are closed (no public bookings).
// Must match MONDAY_CLOSED_FROM in /app/backend/slot_logic.py.
const MONDAY_CLOSED_FROM = "2026-09-07";

// Auto = Friday only; Sunday blocked; Mondays from MONDAY_CLOSED_FROM blocked; otherwise Manual.
function transmissionForDate(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  const wd = d.getDay(); // Sun=0..Sat=6
  if (wd === 0) return null;
  if (wd === 1 && dateStr >= MONDAY_CLOSED_FROM) return null;
  if (wd === 5) return "auto";
  return "manual";
}

function BookingCard({ booking, slotIndex, onOpen, dragHandleId }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: dragHandleId });
  return (
    <div
      ref={setNodeRef}
      className={`bg-[#37B160] text-white rounded-lg p-1.5 text-[11px] leading-tight ${isDragging ? "opacity-30" : "shadow-sm hover:shadow-md"} transition-shadow`}
      data-testid={`booking-card-${booking.booking_id}-${slotIndex}`}
    >
      <div className="flex items-center gap-1">
        <button
          type="button"
          {...listeners}
          {...attributes}
          className="cursor-grab active:cursor-grabbing touch-none p-0.5 -ml-0.5"
          aria-label="Drag to reschedule"
          data-testid={`booking-drag-${booking.booking_id}-${slotIndex}`}
        >
          <GripVertical className="h-3 w-3 opacity-70" />
        </button>
        <button
          type="button"
          onClick={onOpen}
          className="font-medium truncate text-left flex-1 hover:underline"
          data-testid={`booking-open-${booking.booking_id}-${slotIndex}`}
        >
          {booking.customer_name}
        </button>
      </div>
      <div className="opacity-90 text-[10px] uppercase tracking-[0.05em]">
        {booking.transmission === "auto" ? "Auto" : "Manual"} · {booking.product_label?.replace(" (5 lessons)","").replace(" (10 lessons)","") || booking.product}
      </div>
    </div>
  );
}

function DroppableSlot({ id, time, children }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className={`rounded-md min-h-[26px] px-1 py-0.5 transition-colors ${isOver ? "bg-[#37B160]/15 ring-1 ring-[#37B160]" : ""}`}
    >
      <div className="text-[9px] uppercase tracking-[0.1em] text-[#9CA3AF]">{time}</div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

export default function AdminCalendar() {
  const [bookings, setBookings] = useState([]);
  const [holidays, setHolidays] = useState([]); // [{date, time, reason}]
  const [offers, setOffers] = useState([]); // intensive offers (active holds)
  const [month, setMonth] = useState(() => startOfMonth(new Date()));
  const [openBooking, setOpenBooking] = useState(null); // booking obj
  const [openSlotIdx, setOpenSlotIdx] = useState(0);
  const [reschedule, setReschedule] = useState({ date: "", time: "08:00" });
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [selectedSlots, setSelectedSlots] = useState(new Set());
  const [activeDrag, setActiveDrag] = useState(null);
  const [holidayModalOpen, setHolidayModalOpen] = useState(false);
  const [holidayForm, setHolidayForm] = useState({ start_date: "", end_date: "", reason: "", times: [...ADMIN_SLOT_TIMES] });
  const [holidayToRemove, setHolidayToRemove] = useState(null); // {date, time?, reason}

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const load = async () => {
    try {
      const [items, hols, offs] = await Promise.all([
        adminBookings(),
        adminListHolidays(),
        adminListIntensiveOffers().catch(() => []),
      ]);
      setBookings(items);
      setHolidays(hols);
      setOffers(Array.isArray(offs) ? offs : []);
    } catch { toast.error("Could not load bookings"); }
  };
  useEffect(() => { load(); }, []);

  const holidayMap = useMemo(() => {
    // { date: { byTime: {time: {reason}}, reasonList: [..], allBlocked: bool } }
    const m = {};
    holidays.forEach(h => {
      if (!m[h.date]) m[h.date] = { byTime: {}, reasons: new Set() };
      m[h.date].byTime[h.time] = { reason: h.reason || "" };
      if (h.reason) m[h.date].reasons.add(h.reason);
    });
    Object.keys(m).forEach(d => {
      const times = Object.keys(m[d].byTime);
      m[d].allBlocked = ADMIN_SLOT_TIMES.every(t => times.includes(t));
      m[d].reasonList = [...m[d].reasons];
    });
    return m;
  }, [holidays]);

  // Index active intensive-offer holds by `date|time`. Only pending offers
  // whose expiry is still in the future contribute to the held map.
  const heldMap = useMemo(() => {
    const m = {};
    const now = Date.now();
    offers.forEach(o => {
      if (o.status !== "pending") return;
      const exp = o.expires_at ? Date.parse(o.expires_at) : 0;
      if (!exp || exp < now) return;
      (o.slots || []).forEach(s => {
        const k = `${s.date}|${s.time}`;
        if (!m[k]) m[k] = [];
        m[k].push(o);
      });
    });
    return m;
  }, [offers]);

  const openBlockDay = (dateStr) => {
    setHolidayForm({ start_date: dateStr, end_date: dateStr, reason: "", times: [...ADMIN_SLOT_TIMES] });
    setHolidayModalOpen(true);
  };

  const toggleHolidayTime = (t) => {
    setHolidayForm(f => {
      const set = new Set(f.times);
      if (set.has(t)) set.delete(t); else set.add(t);
      return { ...f, times: [...set] };
    });
  };

  const submitHoliday = async () => {
    if (!holidayForm.start_date) {
      toast.error("Pick a start date.");
      return;
    }
    if (!holidayForm.times || holidayForm.times.length === 0) {
      toast.error("Select at least one time slot to block.");
      return;
    }
    try {
      const res = await adminCreateHoliday({
        start_date: holidayForm.start_date,
        end_date: holidayForm.end_date || holidayForm.start_date,
        times: holidayForm.times,
        reason: holidayForm.reason || "",
      });
      const n = res?.blocked?.length || 0;
      toast.success(`Blocked ${n} slot${n === 1 ? "" : "s"}`);
      setHolidayModalOpen(false);
      await load();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not block date(s)"));
    }
  };

  const removeHoliday = async () => {
    if (!holidayToRemove) return;
    try {
      if (holidayToRemove.time) {
        await adminDeleteHolidaySlot(holidayToRemove.date, holidayToRemove.time);
        toast.success(`Unblocked ${holidayToRemove.date} ${holidayToRemove.time}`);
      } else {
        await adminDeleteHoliday(holidayToRemove.date);
        toast.success(`Unblocked all slots on ${holidayToRemove.date}`);
      }
      setHolidayToRemove(null);
      await load();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not unblock"));
    }
  };

  // Compute month grid days (6 weeks max for stable layout)
  const days = useMemo(() => {
    const start = startOfGridForMonth(month);
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(start); d.setDate(start.getDate() + i);
      return d;
    });
  }, [month]);

  // Index bookings by date|time
  const cellMap = useMemo(() => {
    const m = {};
    bookings.forEach(b => {
      if (b.status === "cancelled") return;
      b.slots?.forEach((s, idx) => {
        const k = `${s.date}|${s.time}`;
        if (!m[k]) m[k] = [];
        m[k].push({ booking: b, slotIndex: idx });
      });
    });
    return m;
  }, [bookings]);

  const onDragStart = (e) => {
    const [bid, sidxStr] = e.active.id.split("::");
    const booking = bookings.find(b => b.booking_id === bid);
    if (booking) setActiveDrag({ booking, slotIndex: parseInt(sidxStr, 10) });
  };

  const onDragEnd = async (e) => {
    setActiveDrag(null);
    const { active, over } = e;
    if (!over) return;
    const [bid, sidxStr] = active.id.split("::");
    const slotIndex = parseInt(sidxStr, 10);
    const [date, time] = over.id.split("|");
    const booking = bookings.find(b => b.booking_id === bid);
    if (!booking) return;
    const cur = booking.slots[slotIndex];
    if (cur.date === date && cur.time === time) return;
    try {
      await adminReschedule({ booking_id: bid, slot_index: slotIndex, new_date: date, new_time: time });
      toast.success("Lesson rescheduled");
      await load();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not move lesson"));
    }
  };

  const monthLabel = month.toLocaleDateString("en-GB", { month: "long", year: "numeric" });

  const openCardModal = (booking, slotIndex) => {
    setOpenBooking(booking);
    setOpenSlotIdx(slotIndex);
    setSelectedSlots(new Set([slotIndex])); // multi-select starts with the clicked slot
    setReschedule({
      date: booking.slots[slotIndex]?.date || "",
      time: booking.slots[slotIndex]?.time || "08:00",
    });
  };

  const toggleSlotSelected = (idx) => {
    setSelectedSlots(prev => {
      const next = new Set(prev);
      if (next.has(idx)) { next.delete(idx); } else { next.add(idx); }
      // Keep reschedule form in sync when exactly one slot is selected
      if (next.size === 1) {
        const only = [...next][0];
        setOpenSlotIdx(only);
        setReschedule({
          date: openBooking?.slots[only]?.date || "",
          time: openBooking?.slots[only]?.time || "08:00",
        });
      }
      return next;
    });
  };

  const submitReschedule = async () => {
    if (!openBooking) return;
    if (selectedSlots.size !== 1) {
      toast.error("Please select exactly one slot to reschedule. Bulk reschedule isn't supported — move slots one at a time, or drag them on the calendar.");
      return;
    }
    try {
      await adminReschedule({
        booking_id: openBooking.booking_id,
        slot_index: openSlotIdx,
        new_date: reschedule.date,
        new_time: reschedule.time,
      });
      toast.success("Lesson rescheduled — customer notified.");
      setOpenBooking(null);
      await load();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not reschedule"));
    }
  };

  const submitCancel = async () => {
    if (!openBooking) return;
    const total = openBooking.slots.length;
    const partial = selectedSlots.size > 0 && selectedSlots.size < total;
    const indexes = partial ? [...selectedSlots].sort((a, b) => a - b) : undefined;
    try {
      await adminCancelBooking(openBooking.booking_id, indexes);
      toast.success(partial
        ? `Cancelled ${selectedSlots.size} of ${total} slot${total === 1 ? "" : "s"} — customer notified.`
        : "Booking cancelled — customer notified.");
      setConfirmCancelOpen(false);
      setOpenBooking(null);
      setSelectedSlots(new Set());
      await load();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not cancel booking"));
    }
  };

  const goMonth = (delta) => {
    setMonth(prev => {
      const x = new Date(prev); x.setMonth(prev.getMonth() + delta); return startOfMonth(x);
    });
  };

  return (
    <div data-testid="admin-calendar-page">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl tracking-tight font-medium" style={{fontFamily:"Outfit, sans-serif"}}>Master calendar</h1>
          <p className="text-sm text-[#4B5563]">Drag a booking card by the grip handle to reschedule it to <em>any</em> day or time, or click the customer name to view full details. Transmission/day restrictions only apply to new customer bookings.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="border-black/15" onClick={() => { setHolidayForm({ start_date: "", end_date: "", reason: "", times: [...ADMIN_SLOT_TIMES] }); setHolidayModalOpen(true); }} data-testid="open-holiday-modal">
            <Ban className="h-4 w-4 mr-1.5" /> Block dates
          </Button>
          <Button size="sm" variant="outline" className="border-black/15" onClick={() => goMonth(-1)} data-testid="cal-prev-month"><ChevronLeft className="h-4 w-4" /></Button>
          <Button size="sm" variant="outline" className="border-black/15 min-w-[160px]" onClick={() => setMonth(startOfMonth(new Date()))} data-testid="cal-today">
            <CalendarIcon className="h-4 w-4 mr-1.5" /> {monthLabel}
          </Button>
          <Button size="sm" variant="outline" className="border-black/15" onClick={() => goMonth(1)} data-testid="cal-next-month"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
        <div className="mt-6 bg-white border border-black/5 rounded-2xl overflow-hidden">
          {/* Weekday header */}
          <div className="grid grid-cols-7 bg-[#F9FAFB] border-b border-black/5">
            {WEEKDAYS.map((d, i) => (
              <div key={d} className={`px-3 py-2 text-[10px] uppercase tracking-[0.2em] font-semibold ${i >= 5 ? "text-[#9CA3AF]" : "text-[#4B5563]"}`}>{d}</div>
            ))}
          </div>
          {/* Day grid */}
          <div className="grid grid-cols-7" data-testid="month-grid">
            {days.map((d, idx) => {
              const ds = isoDate(d);
              const inMonth = d.getMonth() === month.getMonth();
              const wd = d.getDay();
              const isSunday = wd === 0;
              const isToday = ds === isoDate(new Date());
              const dayTx = transmissionForDate(ds);
              // Sundays + closed-Mondays render as grey "closed" cells.
              const isClosed = !dayTx;
              const holidayInfo = holidayMap[ds]; // {byTime, allBlocked, reasonList} or undefined
              const allBlocked = !!holidayInfo?.allBlocked;
              return (
                <div key={idx}
                     className={`group relative border-t border-l border-black/5 min-h-[140px] p-1.5 ${allBlocked ? "bg-amber-50/60" : isClosed ? "bg-[#F9FAFB]" : "bg-white"} ${!inMonth ? "opacity-40" : ""} ${(idx + 1) % 7 === 0 ? "border-r-0" : ""}`}
                     data-testid={`cal-day-${ds}`}>
                  <div className="flex items-baseline justify-between mb-1">
                    <span className={`text-xs font-medium ${isToday ? "h-5 w-5 inline-grid place-items-center rounded-full bg-[#111827] text-white" : "text-[#111827]"}`} style={{fontFamily:"Outfit, sans-serif"}}>
                      {d.getDate()}
                    </span>
                    {dayTx && inMonth && !allBlocked && (
                      <span className={`text-[9px] uppercase tracking-[0.1em] font-medium ${dayTx === "auto" ? "text-[#37B160]" : "text-[#4B5563]"}`}>{dayTx}</span>
                    )}
                    {!allBlocked && inMonth && dayTx && (
                      <button
                        type="button"
                        onClick={() => openBlockDay(ds)}
                        title="Block slot(s) on this day"
                        className="opacity-0 group-hover:opacity-100 transition-opacity h-5 w-5 grid place-items-center rounded-md text-[#9CA3AF] hover:text-amber-600 hover:bg-amber-50 absolute right-1 top-1"
                        data-testid={`block-day-${ds}`}
                      >
                        <Ban className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                  {allBlocked ? (
                    <button
                      type="button"
                      onClick={() => setHolidayToRemove({ date: ds, reason: holidayInfo.reasonList[0] || "" })}
                      className="w-full text-left rounded-md border border-amber-300 bg-amber-100/70 hover:bg-amber-100 px-2 py-2 transition-colors"
                      data-testid={`holiday-badge-${ds}`}
                    >
                      <div className="flex items-center gap-1.5">
                        <Plane className="h-3 w-3 text-amber-700 flex-none" />
                        <span className="text-[10px] uppercase tracking-[0.1em] font-semibold text-amber-800">Holiday</span>
                      </div>
                      {holidayInfo.reasonList[0] && (
                        <div className="text-[11px] text-amber-900 mt-1 line-clamp-2">{holidayInfo.reasonList[0]}</div>
                      )}
                      <div className="text-[9px] text-amber-700/70 mt-1 italic">Click to remove</div>
                    </button>
                  ) : dayTx ? (
                    <div className="space-y-0.5">
                      {ADMIN_SLOT_TIMES.map(t => {
                        const cellId = `${ds}|${t}`;
                        const items = cellMap[cellId] || [];
                        const slotBlocked = holidayInfo?.byTime?.[t];
                        const heldHere = heldMap[cellId];
                        if (slotBlocked) {
                          return (
                            <button
                              key={t}
                              type="button"
                              onClick={() => setHolidayToRemove({ date: ds, time: t, reason: slotBlocked.reason })}
                              className="w-full rounded-md border border-amber-300 bg-amber-100/70 hover:bg-amber-100 px-1.5 py-1 text-left transition-colors"
                              data-testid={`holiday-slot-${ds}-${t.replace(':','')}`}
                              title={slotBlocked.reason ? `Holiday: ${slotBlocked.reason}` : "Holiday — click to remove"}
                            >
                              <div className="text-[9px] uppercase tracking-[0.1em] text-amber-700/80">{t}</div>
                              <div className="flex items-center gap-1 text-amber-900">
                                <Plane className="h-2.5 w-2.5 flex-none" />
                                <span className="text-[10px] font-semibold uppercase tracking-[0.05em]">Holiday</span>
                              </div>
                            </button>
                          );
                        }
                        return (
                          <DroppableSlot key={t} id={cellId} time={t}>
                            {items.map(({ booking, slotIndex }) => (
                              <BookingCard
                                key={`${booking.booking_id}-${slotIndex}`}
                                booking={booking}
                                slotIndex={slotIndex}
                                onOpen={() => openCardModal(booking, slotIndex)}
                                dragHandleId={`${booking.booking_id}::${slotIndex}`}
                              />
                            ))}
                            {items.length === 0 && heldHere && (
                              <HeldBadge offer={heldHere[0]} />
                            )}
                          </DroppableSlot>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="space-y-0.5">
                      <div className="text-[10px] text-[#9CA3AF] italic mb-1">Closed to new bookings</div>
                      {ADMIN_SLOT_TIMES.map(t => {
                        const cellId = `${ds}|${t}`;
                        const items = cellMap[cellId] || [];
                        const heldHere = heldMap[cellId];
                        return (
                          <DroppableSlot key={t} id={cellId} time={t}>
                            {items.map(({ booking, slotIndex }) => (
                              <BookingCard
                                key={`${booking.booking_id}-${slotIndex}`}
                                booking={booking}
                                slotIndex={slotIndex}
                                onOpen={() => openCardModal(booking, slotIndex)}
                                dragHandleId={`${booking.booking_id}::${slotIndex}`}
                              />
                            ))}
                            {items.length === 0 && heldHere && (
                              <HeldBadge offer={heldHere[0]} />
                            )}
                          </DroppableSlot>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <DragOverlay>
          {activeDrag && (
            <div className="bg-[#37B160] text-white rounded-lg p-1.5 text-[11px] leading-tight shadow-2xl pointer-events-none">
              <div className="font-medium">{activeDrag.booking.customer_name}</div>
              <div className="opacity-90 text-[10px] uppercase tracking-[0.05em]">
                {activeDrag.booking.transmission === "auto" ? "Auto" : "Manual"} · slot {activeDrag.slotIndex + 1}
              </div>
            </div>
          )}
        </DragOverlay>
      </DndContext>

      {/* Manual booking — for lessons paid outside the website */}
      <ManualBookingForm onCreated={load} />

      {/* Booking detail modal */}
      <Dialog open={!!openBooking} onOpenChange={o => !o && setOpenBooking(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="appointment-detail-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              {openBooking?.customer_name}
              <Badge className={openBooking?.transmission === "auto" ? "bg-[#37B160]/15 text-[#37B160] border-[#37B160]/30" : "bg-[#F3F4F6] text-[#4B5563]"}>
                {openBooking?.transmission === "auto" ? "Automatic" : "Manual"}
              </Badge>
            </DialogTitle>
          </DialogHeader>

          {openBooking && (
            <div className="space-y-5 text-sm">
              {/* Customer contact */}
              <div className="bg-[#F9FAFB] rounded-xl p-4 space-y-2">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF] font-semibold">Contact</div>
                <a href={`mailto:${openBooking.customer_email}`} className="flex items-center gap-2 text-[#111827] hover:text-[#37B160]" data-testid="modal-email">
                  <Mail className="h-4 w-4 text-[#37B160]" /> {openBooking.customer_email}
                </a>
                <a href={`tel:${(openBooking.customer_phone || "").replace(/\s/g, "")}`} className="flex items-center gap-2 text-[#111827] hover:text-[#37B160]" data-testid="modal-phone">
                  <Phone className="h-4 w-4 text-[#37B160]" /> {openBooking.customer_phone || "—"}
                </a>
                <div className="flex items-start gap-2 text-[#111827]" data-testid="modal-address">
                  <MapPin className="h-4 w-4 text-[#37B160] mt-0.5 flex-none" />
                  <span>
                    {[openBooking.pickup_address, openBooking.pickup_town, openBooking.pickup_postcode].filter(Boolean).join(", ") || "—"}
                  </span>
                </div>
              </div>

              {/* Booking details */}
              <div className="grid grid-cols-2 gap-3">
                <Info icon={Car} label="Product" value={openBooking.product_label || openBooking.product} />
                <Info icon={Hash} label="Order ID" value={openBooking.order_id || "—"} mono />
                <Info icon={CalendarIcon} label="Amount paid" value={`£${(openBooking.amount ?? 0).toFixed(2)}`} />
                <Info icon={Hash} label="Status" value={openBooking.status} pill />
              </div>

              {/* Notes */}
              {openBooking.notes && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF] font-semibold mb-1">Customer notes</div>
                  <p className="text-[#4B5563] whitespace-pre-wrap">{openBooking.notes}</p>
                </div>
              )}

              {/* All slots in booking — multi-select */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF] font-semibold">All lessons in this booking</div>
                  <div className="text-[10px] text-[#9CA3AF]">{selectedSlots.size} of {openBooking.slots.length} selected</div>
                </div>
                <ul className="space-y-1">
                  {openBooking.slots.map((s, i) => {
                    const checked = selectedSlots.has(i);
                    return (
                      <li key={i} className={`text-sm flex items-center justify-between rounded-lg px-3 py-2 transition-colors ${checked ? "bg-[#37B160]/10 ring-1 ring-[#37B160]/30" : "bg-[#F9FAFB] hover:bg-[#F3F4F6]"}`} data-testid={`modal-slot-${i}`}>
                        <label className="flex items-center gap-3 cursor-pointer flex-1">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleSlotSelected(i)}
                            className="h-4 w-4 accent-[#37B160] cursor-pointer"
                            data-testid={`modal-slot-checkbox-${i}`}
                          />
                          <span>{formatLongDate(s.date)} · {s.time}–{endTime(s.time)}</span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
                <p className="text-[11px] text-[#9CA3AF] mt-2">Tick one slot to reschedule, or multiple to cancel a subset of the booking.</p>
              </div>

              {/* Reschedule */}
              <div className="border-t border-black/5 pt-4">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF] font-semibold mb-2">
                  Reschedule {selectedSlots.size === 1 ? "selected slot" : <span className="text-red-500 normal-case tracking-normal">— select exactly one slot above</span>}
                </div>
                <div className={`grid grid-cols-2 gap-2 ${selectedSlots.size !== 1 ? "opacity-50 pointer-events-none" : ""}`}>
                  <Input type="date" value={reschedule.date} onChange={e => setReschedule({...reschedule, date: e.target.value})} data-testid="reschedule-date" />
                  <Select value={reschedule.time} onValueChange={v => setReschedule({...reschedule, time: v})}>
                    <SelectTrigger data-testid="reschedule-time"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ADMIN_SLOT_TIMES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-xs text-[#9CA3AF] mt-2">Admin override: you can move this lesson to any day, including off-transmission days or Sundays. Customer is emailed automatically.</p>
              </div>
            </div>
          )}

          <DialogFooter className="sm:justify-between flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => setConfirmCancelOpen(true)}
              disabled={openBooking?.status === "cancelled"}
              className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
              data-testid="cancel-booking-btn"
            >
              {openBooking?.status === "cancelled"
                ? "Already cancelled"
                : (selectedSlots.size > 0 && selectedSlots.size < (openBooking?.slots?.length || 0)
                    ? `Cancel ${selectedSlots.size} selected slot${selectedSlots.size === 1 ? "" : "s"}`
                    : "Cancel whole booking")}
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setOpenBooking(null)} className="border-black/15">Close</Button>
              <Button onClick={submitReschedule} disabled={openBooking?.status === "cancelled" || selectedSlots.size !== 1} className="bg-[#37B160] hover:bg-[#2D9450] text-white" data-testid="reschedule-save-btn">
                {selectedSlots.size > 1 ? "Reschedule (pick 1)" : "Save & notify customer"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm-cancel dialog (rendered outside the booking modal so it stacks correctly) */}
      <AlertDialog open={confirmCancelOpen} onOpenChange={setConfirmCancelOpen}>
        <AlertDialogContent data-testid="confirm-cancel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {selectedSlots.size > 0 && selectedSlots.size < (openBooking?.slots?.length || 0)
                ? `Cancel ${selectedSlots.size} of ${openBooking?.slots?.length} slots?`
                : "Cancel this booking?"}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                {selectedSlots.size > 0 && selectedSlots.size < (openBooking?.slots?.length || 0) ? (
                  <>
                    The following slot{selectedSlots.size === 1 ? "" : "s"} will be removed from <strong>{openBooking?.customer_name}</strong>'s booking:
                    <ul className="mt-3 list-disc pl-5 space-y-1">
                      {[...selectedSlots].sort((a,b)=>a-b).map(i => {
                        const s = openBooking?.slots?.[i];
                        return <li key={i}>{s?.date} at {s?.time}</li>;
                      })}
                    </ul>
                    <p className="mt-3">The rest of the booking ({(openBooking?.slots?.length || 0) - selectedSlots.size} slot{(openBooking?.slots?.length || 0) - selectedSlots.size === 1 ? "" : "s"}) stays active. Customer will be emailed. Refunds handled separately.</p>
                  </>
                ) : (
                  <>
                    This will cancel the entire booking for <strong>{openBooking?.customer_name}</strong>.
                    <ul className="mt-3 list-disc pl-5 space-y-1">
                      <li>All {openBooking?.slots?.length || 0} lesson{(openBooking?.slots?.length || 0) === 1 ? "" : "s"} will be removed from the calendar.</li>
                      <li>The order will be marked <strong>CANCELLED</strong>.</li>
                      <li>Customer will be emailed.</li>
                      <li>Refunds are handled separately once Stripe is connected.</li>
                    </ul>
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel-no">Keep booking</AlertDialogCancel>
            <AlertDialogAction
              onClick={submitCancel}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-cancel-yes"
            >
              {selectedSlots.size > 0 && selectedSlots.size < (openBooking?.slots?.length || 0)
                ? `Yes, cancel ${selectedSlots.size} slot${selectedSlots.size === 1 ? "" : "s"}`
                : "Yes, cancel booking"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Block dates (holiday) modal */}
      <Dialog open={holidayModalOpen} onOpenChange={setHolidayModalOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="holiday-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plane className="h-4 w-4 text-amber-700" /> Block slots for a holiday
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <p className="text-[#4B5563]">Blocked slots appear as <strong>Booked</strong> to customers and show a holiday badge here. Pre-existing bookings on those slots must be cancelled first.</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#9CA3AF]">Start date</label>
                <Input type="date" value={holidayForm.start_date}
                       onChange={e => setHolidayForm(f => ({ ...f, start_date: e.target.value, end_date: f.end_date || e.target.value }))}
                       data-testid="holiday-start" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#9CA3AF]">End date</label>
                <Input type="date" value={holidayForm.end_date}
                       onChange={e => setHolidayForm(f => ({ ...f, end_date: e.target.value }))}
                       data-testid="holiday-end" />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#9CA3AF]">Slots to block</label>
                <button
                  type="button"
                  onClick={() => setHolidayForm(f => ({ ...f, times: f.times.length === ADMIN_SLOT_TIMES.length ? [] : [...ADMIN_SLOT_TIMES] }))}
                  className="text-[10px] uppercase tracking-[0.1em] text-amber-700 hover:text-amber-800 font-semibold"
                  data-testid="holiday-times-toggle-all"
                >
                  {holidayForm.times.length === ADMIN_SLOT_TIMES.length ? "Clear all" : "Select all"}
                </button>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {ADMIN_SLOT_TIMES.map(t => {
                  const checked = holidayForm.times.includes(t);
                  return (
                    <label key={t}
                           className={`flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer transition-colors ${checked ? "bg-amber-50 border-amber-300" : "bg-white border-black/10 hover:border-black/20"}`}
                           data-testid={`holiday-time-${t.replace(':','')}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleHolidayTime(t)}
                        className="h-4 w-4 accent-amber-600 cursor-pointer"
                      />
                      <span className="text-sm font-medium">{t}</span>
                    </label>
                  );
                })}
              </div>
              <p className="text-[11px] text-[#9CA3AF] mt-2">Tick all three to block the whole day off; tick one or two to keep the rest available.</p>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#9CA3AF]">Reason (optional, admin-only)</label>
              <Input value={holidayForm.reason}
                     onChange={e => setHolidayForm(f => ({ ...f, reason: e.target.value }))}
                     placeholder="e.g. Family holiday, vehicle service"
                     data-testid="holiday-reason" />
            </div>

            {holidays.length > 0 && (
              <div className="border-t border-black/5 pt-4">
                <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#9CA3AF] mb-2">Currently blocked ({holidays.length} slot{holidays.length === 1 ? "" : "s"})</div>
                <ul className="space-y-1 max-h-40 overflow-y-auto" data-testid="holiday-list">
                  {holidays.map(h => (
                    <li key={`${h.date}-${h.time}`} className="flex items-center justify-between text-sm bg-amber-50 border border-amber-100 rounded-md px-2 py-1.5">
                      <div>
                        <div className="font-medium text-amber-900">{h.date} · {h.time}</div>
                        {h.reason && <div className="text-[11px] text-amber-700">{h.reason}</div>}
                      </div>
                      <button type="button" onClick={() => setHolidayToRemove({ date: h.date, time: h.time, reason: h.reason })}
                              className="text-amber-700 hover:text-red-600 p-1"
                              data-testid={`holiday-remove-${h.date}-${h.time.replace(':','')}`}
                              title="Remove">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-black/15" onClick={() => setHolidayModalOpen(false)}>Close</Button>
            <Button onClick={submitHoliday} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="holiday-submit">
              <Plus className="h-4 w-4 mr-1" /> Block slot{holidayForm.times.length === 1 ? "" : "s"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm unblock */}
      <AlertDialog open={!!holidayToRemove} onOpenChange={(o) => !o && setHolidayToRemove(null)}>
        <AlertDialogContent data-testid="confirm-unblock-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {holidayToRemove?.time
                ? `Unblock ${holidayToRemove.date} ${holidayToRemove.time}?`
                : `Unblock all slots on ${holidayToRemove?.date}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {holidayToRemove?.time
                ? "Customers will be able to book this specific slot again immediately."
                : "Every slot on this day will become bookable again."}
              {holidayToRemove?.reason && <span className="block mt-2 text-amber-700">Reason: {holidayToRemove.reason}</span>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep blocked</AlertDialogCancel>
            <AlertDialogAction onClick={removeHoliday} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="confirm-unblock-yes">
              Unblock
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function HeldBadge({ offer }) {
  const name = offer?.customer_name || "customer";
  const title = `Held for intensive offer — ${name}`;
  return (
    <Link
      to="/admin/intensive-offers"
      title={title}
      className="block rounded-md border border-dashed border-amber-400/70 bg-amber-50/80 hover:bg-amber-100 px-1.5 py-1 transition-colors"
      data-testid={`held-offer-${offer?.offer_id || "x"}`}
    >
      <div className="flex items-center gap-1 text-amber-800">
        <Hourglass className="h-2.5 w-2.5 flex-none" />
        <span className="text-[10px] font-semibold uppercase tracking-[0.05em] truncate">
          Held · intensive
        </span>
      </div>
      <div className="text-[10px] text-amber-900/80 truncate">{name}</div>
    </Link>
  );
}

function Info({ icon: Icon, label, value, mono, pill }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF] font-semibold mb-1">{label}</div>
      <div className="flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5 text-[#37B160] flex-none" />
        {pill ? (
          <Badge className="bg-[#37B160] text-white">{value}</Badge>
        ) : (
          <span className={`text-[#111827] ${mono ? "font-mono text-xs" : ""}`}>{value}</span>
        )}
      </div>
    </div>
  );
}
